<template>
  <ion-page>
    <ion-content class="welcome-content">
      <div class="background-scene">
        <div class="stars"></div>
      </div>
      
      <div class="user-settings-header">
        <ion-button fill="clear" class="back-button" @click="goBack">
          <ion-icon :icon="chevronBack"></ion-icon>
          Back
        </ion-button>
        <h1 class="user-settings-title">User Settings</h1>
      </div>

      <form @submit.prevent="saveSettings" class="user-settings-form">
        <div class="form-fields-grid">
          <div class="user-input-group">
            <ion-label>Username</ion-label>
            <input 
              type="text" 
              v-model="user.username" 
              placeholder="Enter username"
              class="user-custom-input"
            />
          </div>

          <div class="user-input-group">
            <ion-label>Email</ion-label>
            <input 
              type="email" 
              v-model="user.email" 
              placeholder="Enter email"
              class="user-custom-input"
            />
          </div>

          <div class="user-input-group">
            <ion-label>New Password</ion-label>
            <input 
              type="password" 
              v-model="newPassword" 
              placeholder="Enter new password"
              class="user-custom-input password-input"
              autocomplete="new-password"
            />
          </div>

          <div class="user-input-group">
            <ion-label>Confirm Password</ion-label>
            <input 
              type="password" 
              v-model="confirmPassword" 
              placeholder="Confirm new password"
              class="user-custom-input password-input"
            />
          </div>

          <div class="user-input-group subscription-group">
            <ion-label>Subscription Level</ion-label>
            <div class="subscription-toggle">
              <div class="subscription-option" :class="{ active: subscriptionLevel === 'basic' }"
                @click="subscriptionLevel = 'basic'">
                <div class="option-title">Basic Plan</div>
                <div class="option-price">$4.99/month</div>
                <div class="option-features">
                  <div class="feature-item">
                    <ion-icon :icon="checkmarkCircle" class="feature-icon"></ion-icon>
                    <span>Up to 1 profile</span>
                  </div>
                  <div class="feature-item">
                    <ion-icon :icon="checkmarkCircle" class="feature-icon"></ion-icon>
                    <span>Basic calendar features</span>
                  </div>
                  <div class="feature-item">
                    <ion-icon :icon="checkmarkCircle" class="feature-icon"></ion-icon>
                    <span>Standard support</span>
                  </div>
                </div>
              </div>
              <div class="subscription-option" :class="{ active: subscriptionLevel === 'accountant' }"
                @click="subscriptionLevel = 'accountant'">
                <div class="option-title">Premium Plan</div>
                <div class="option-price">$9.99/month</div>
                <div class="option-features">
                  <div class="feature-item">
                    <ion-icon :icon="checkmarkCircle" class="feature-icon"></ion-icon>
                    <span>Unlimited profiles</span>
                  </div>
                  <div class="feature-item">
                    <ion-icon :icon="checkmarkCircle" class="feature-icon"></ion-icon>
                    <span>Advanced calendar features</span>
                  </div>
                  <div class="feature-item">
                    <ion-icon :icon="checkmarkCircle" class="feature-icon"></ion-icon>
                    <span>Priority support</span>
                  </div>
                </div>
              </div>
              <div class="subscription-option" :class="{ active: subscriptionLevel === 'cancel' }"
                @click="subscriptionLevel = 'cancel'">
                <div class="option-title">Cancel Plan</div>
                <div class="option-price">$0/month</div>
                <div class="option-features">
                  <div class="feature-item">
                    <ion-icon :icon="warning" class="feature-icon warning"></ion-icon>
                    <span>Access will cease at the end of your current pay period</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="user-input-group">
            <ion-label>Theme</ion-label>
            <select v-model="theme" class="user-custom-input">
              <option value="light">Light</option>
              <option value="dark">Dark</option>
            </select>
          </div>
        </div>

        <div class="user-form-actions">
          <ion-button class="cancel-button" @click="goBack">
            Cancel
          </ion-button>
          <ion-button type="submit" class="save-button">
            Save Changes
          </ion-button>
        </div>
      </form>

      <!-- Toast Notification -->
      <div v-if="showToast" class="toast-notification" :class="toastType">
        <div class="toast-content">
          <ion-icon :icon="toastIcon" class="toast-icon"></ion-icon>
          <span class="toast-message">{{ toastMessage }}</span>
        </div>
        <ion-button fill="clear" class="toast-close" @click="hideToast">
          <ion-icon :icon="close"></ion-icon>
        </ion-button>
      </div>
    </ion-content>
  </ion-page>
</template>

<script>
import {
  IonPage,
  IonContent,
  IonButton,
  IonLabel,
  IonIcon
} from '@ionic/vue';
import { chevronBack, checkmarkCircle, warning, checkmark, close, alertCircle } from 'ionicons/icons';
import axios from 'axios';
import {getAPIURL} from '../logic/api';

export default {
  name: 'UserSettings',
  components: {
    IonPage,
    IonContent,
    IonButton,
    IonLabel,
    IonIcon
  },
  data() {
    return {
      chevronBack,
      checkmarkCircle,
      warning,
      checkmark,
      close,
      alertCircle,
      user: {},
      newPassword: '',
      confirmPassword: '',
      subscriptionLevel: 'basic',
      theme: 'dark',
      showToast: false,
      toastMessage: '',
      toastType: 'success',
      toastIcon: checkmark,
    }
  },
  methods: {
    goBack() {
      this.$router.go(-1);
    },
    showSuccessToast(message) {
      this.toastMessage = message;
      this.toastType = 'success';
      this.toastIcon = this.checkmark;
      this.showToast = true;
      setTimeout(() => {
        this.hideToast();
      }, 4000);
    },
    showErrorToast(message) {
      this.toastMessage = message;
      this.toastType = 'error';
      this.toastIcon = this.alertCircle;
      this.showToast = true;
      setTimeout(() => {
        this.hideToast();
      }, 5000);
    },
    hideToast() {
      this.showToast = false;
    },
    async fetchUserSettings() {
      try {
        const userID = localStorage.getItem('userID');
        const response = await axios.get(`https://budgeteer.x01r.xyz/api/user/get-user?userID=${userID}`);
        this.user = response.data.user;
      } catch (error) {
        console.error('Error fetching user info:', error);
      }
    },
    loadTheme() {
      const savedTheme = localStorage.getItem('theme') || 'dark';
      this.theme = savedTheme;
      this.applyTheme();
    },
    applyTheme() {
      document.body.classList.remove('dark-theme', 'light-theme');
      if (this.theme === 'dark') {
        document.body.classList.add('dark-theme');
      } else if (this.theme === 'light') {
        document.body.classList.add('light-theme');
      }
    },
    async saveSettings() {
      try {
        // Validate that both password fields have values
        if (!this.newPassword.trim() || !this.confirmPassword.trim()) {
          this.showErrorToast('Please enter both new password and confirm password to save your settings');
          return;
        }

        // Validate that passwords match
        if (this.newPassword !== this.confirmPassword) {
          this.showErrorToast('Passwords do not match');
          return;
        }

        const updateData = {
          userID: this.user.id,
          username: this.user.username,
          password: this.newPassword,
          email: this.user.email,
          subscriptionLevel: this.subscriptionLevel
        };

        console.log('=== USER SETTINGS UPDATE ATTEMPT ===');
        console.log('Updating user with data:', {
          userID: updateData.userID,
          username: updateData.username,
          email: updateData.email,
          subscriptionLevel: updateData.subscriptionLevel,
          password: '[HIDDEN]',
          theme: this.theme
        });

        await axios.put(getAPIURL()+'/api/user/update-user', updateData);

        // Clear password fields after successful update
        this.newPassword = '';
        this.confirmPassword = '';
        localStorage.setItem('theme', this.theme);
        this.applyTheme();

        console.log('=== USER SETTINGS UPDATE SUCCESS ===');
        console.log('Successfully updated user settings for:', {
          userID: this.user.id,
          username: this.user.username,
          email: this.user.email,
          subscriptionLevel: this.subscriptionLevel,
          theme: this.theme,
          timestamp: new Date().toISOString()
        });

        this.showSuccessToast('Settings updated successfully');
      } catch (error) {
        console.error('=== USER SETTINGS UPDATE FAILURE ===');
        console.error('Failed to update user settings for:', {
          userID: this.user.id,
          username: this.user.username,
          email: this.user.email,
          subscriptionLevel: this.subscriptionLevel,
          theme: this.theme,
          error: error.message,
          timestamp: new Date().toISOString()
        });
        console.error('Full error details:', error);
        this.showErrorToast('Failed to update settings');
      }
    }
  },
  mounted() {
    this.fetchUserSettings();
    this.loadTheme();
  },
  watch: {
    theme(newTheme) {
      this.applyTheme();
      localStorage.setItem('theme', newTheme);
    }
  }
}
</script>